import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../config/api';
import { FiPlus, FiEdit, FiTrash2, FiX, FiSave } from 'react-icons/fi';
import { useResizableColumns } from '../../hooks/useResizableColumns';
import LoadingButton from '../LoadingButton';
import IconButton from '../IconButton';
import Modal from '../Modal';
import MoreMenu from '../MoreMenu';
import { appAlert, appConfirm } from '../../utils/appDialog';
import ListPager from '../ListPager';
import { useClientPager } from '../../hooks/useClientPager';

function isActiveValue(value) {
  return value === 1 || value === true;
}

function emptyFormFromFields(fields) {
  return fields.reduce((acc, field) => {
    if (field.type === 'active-select') {
      acc[field.key] = true;
    } else if (field.type === 'checkbox') {
      acc[field.key] = field.defaultValue === true;
    } else if (field.type === 'number') {
      acc[field.key] = field.defaultValue ?? '';
    } else {
      acc[field.key] = '';
    }
    return acc;
  }, {});
}

function rowToFormData(row, fields) {
  const data = emptyFormFromFields(fields);
  for (const field of fields) {
    if (field.type === 'active-select' || field.type === 'checkbox') {
      data[field.key] = isActiveValue(row[field.key]);
    } else if (field.type === 'number') {
      data[field.key] = row[field.key] ?? '';
    } else {
      data[field.key] = row[field.key] ?? '';
    }
  }
  return data;
}

function renderCellValue(column, row) {
  const value = row[column.key];
  if (column.type === 'status') {
    const active = isActiveValue(value);
    return (
      <span className={`status-badge ${active ? 'active' : 'inactive'}`}>
        {active ? 'Active' : 'Inactive'}
      </span>
    );
  }
  if (column.type === 'bool') {
    return isActiveValue(value) ? 'Yes' : 'No';
  }
  if (column.key === 'name') {
    return <strong>{value}</strong>;
  }
  return value ?? '-';
}

export default function MetadataEntriesPanel({ type, enabled = true }) {
  const queryClient = useQueryClient();
  const [showModal, setShowModal] = useState(false);
  const [editingRow, setEditingRow] = useState(null);
  const [formData, setFormData] = useState(() => emptyFormFromFields(type.formFields));

  const listUrl = useMemo(() => {
    const params = new URLSearchParams(type.listParams || {});
    const qs = params.toString();
    return qs ? `${type.apiBase}?${qs}` : type.apiBase;
  }, [type.apiBase, type.listParams]);

  const { data: rows = [], isLoading } = useQuery(
    [type.queryKey, listUrl],
    () => api.get(listUrl).then((res) => res.data),
    { enabled }
  );

  const {
    pageItems: pagedRows,
    pagination: metadataPagination,
    setPage: setMetadataPage,
  } = useClientPager(rows, { resetKey: listUrl });

  const invalidate = () => {
    queryClient.invalidateQueries(type.cacheRootKey || type.queryKey);
  };

  const createMutation = useMutation(
    (data) => api.post(type.apiBase, data).then((res) => res.data),
    {
      onSuccess: () => {
        invalidate();
        setShowModal(false);
        setFormData(emptyFormFromFields(type.formFields));
        setEditingRow(null);
      },
      onError: (error) => {
        void appAlert(`Failed to create ${type.singularLabel.toLowerCase()}: ${error.response?.data?.error || error.message}`);
      },
    }
  );

  const updateMutation = useMutation(
    ({ id, data }) => api.put(`${type.apiBase}/${id}`, data).then((res) => res.data),
    {
      onSuccess: () => {
        invalidate();
        setShowModal(false);
        setFormData(emptyFormFromFields(type.formFields));
        setEditingRow(null);
      },
      onError: (error) => {
        void appAlert(`Failed to update ${type.singularLabel.toLowerCase()}: ${error.response?.data?.error || error.message}`);
      },
    }
  );

  const deleteMutation = useMutation(
    (id) => api.delete(`${type.apiBase}/${id}`).then((res) => res.data),
    {
      onSuccess: invalidate,
      onError: (error) => {
        void appAlert(`Failed to delete ${type.singularLabel.toLowerCase()}: ${error.response?.data?.error || error.message}`);
      },
    }
  );

  const columnWidths = useMemo(
    () => [...type.columns.map((c) => c.width || 140), 120],
    [type.columns]
  );

  const { getColumnProps, ResizeHandle, tableRef } = useResizableColumns(
    columnWidths,
    `metadata-${type.id}-column-widths`
  );

  const handleAdd = () => {
    setEditingRow(null);
    setFormData(emptyFormFromFields(type.formFields));
    setShowModal(true);
  };

  const handleEdit = (row) => {
    setEditingRow(row);
    setFormData(rowToFormData(row, type.formFields));
    setShowModal(true);
  };

  const handleDelete = async (row) => {
    const label = row[type.nameField] || type.singularLabel;
    if (await appConfirm(`Are you sure you want to delete "${label}"?`)) {
      deleteMutation.mutate(row.id);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingRow) {
      updateMutation.mutate({ id: editingRow.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const renderFormField = (field) => {
    const value = formData[field.key];

    if (field.type === 'textarea') {
      return (
        <textarea
          value={value}
          onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
          placeholder={field.placeholder}
          rows={field.rows || 3}
        />
      );
    }

    if (field.type === 'active-select') {
      return (
        <select
          value={value ? 'true' : 'false'}
          onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value === 'true' })}
        >
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
      );
    }

    if (field.type === 'checkbox') {
      return (
        <label className="metadata-checkbox-label">
          <input
            type="checkbox"
            checked={Boolean(value)}
            onChange={(e) => setFormData({ ...formData, [field.key]: e.target.checked })}
          />
          {' '}
          {field.hint || 'Enabled'}
        </label>
      );
    }

    if (field.type === 'number') {
      return (
        <input
          type="number"
          value={value}
          onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
          placeholder={field.placeholder}
          required={field.required}
        />
      );
    }

    return (
      <input
        type="text"
        value={value}
        onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
        placeholder={field.placeholder}
        required={field.required}
      />
    );
  };

  const TypeIcon = type.icon;

  return (
    <section className="metadata-section" aria-labelledby={`metadata-section-${type.id}`}>
      <div className="metadata-section-head">
        <div className="metadata-section-head-text">
          <h2 id={`metadata-section-${type.id}`}>
            {TypeIcon && <TypeIcon aria-hidden />}
            {type.label}
          </h2>
          <p className="section-description">{type.description}</p>
        </div>
        <div className="metadata-section-actions">
          <IconButton
            icon={FiPlus}
            label={`Add ${type.singularLabel.toLowerCase()}`}
            variant="primary"
            onClick={handleAdd}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="loading">Loading {type.label.toLowerCase()}…</div>
      ) : rows.length === 0 ? (
        <div className="empty-state">
          <p>
            No {type.label.toLowerCase()} yet. Click &quot;Add {type.singularLabel.toLowerCase()}&quot; to
            create one.
          </p>
        </div>
      ) : (
        <div className="metadata-table-container">
          <table ref={tableRef} className="metadata-table" style={{ tableLayout: 'fixed', width: '100%' }}>
            <thead>
              <tr>
                {type.columns.map((column, index) => (
                  <th key={column.key} {...getColumnProps(index)}>
                    {column.label}
                    <ResizeHandle index={index} />
                  </th>
                ))}
                <th {...getColumnProps(type.columns.length)}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pagedRows.map((row) => (
                <tr key={row.id} className="metadata-row">
                  {type.columns.map((column) => (
                    <td key={column.key}>{renderCellValue(column, row)}</td>
                  ))}
                  <td>
                    <MoreMenu
                      label={`Actions for ${row[type.nameField]}`}
                      size="sm"
                      items={[
                        {
                          id: 'edit',
                          label: 'Edit',
                          icon: FiEdit,
                          onSelect: () => handleEdit(row),
                        },
                        {
                          id: 'delete',
                          label: 'Delete',
                          icon: FiTrash2,
                          danger: true,
                          disabled:
                            deleteMutation.isLoading && deleteMutation.variables === row.id,
                          onSelect: () => handleDelete(row),
                        },
                      ]}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <ListPager
        page={metadataPagination.page}
        pageCount={metadataPagination.pageCount}
        total={metadataPagination.total}
        limit={metadataPagination.limit}
        onPageChange={setMetadataPage}
      />

      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        ariaLabel={editingRow ? `Edit ${type.singularLabel.toLowerCase()}` : `Add ${type.singularLabel.toLowerCase()}`}
      >
        <div className="modal-header">
          <h2>{editingRow ? `Edit ${type.singularLabel}` : `Add ${type.singularLabel}`}</h2>
          <button type="button" className="btn-close-modal" onClick={() => setShowModal(false)} aria-label="Close">
            <FiX />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="metadata-entry-form">
          {type.formFields.map((field) => (
            <div key={field.key} className="form-group">
              <label>
                {field.label}
                {field.required ? ' *' : ''}
              </label>
              {renderFormField(field)}
            </div>
          ))}

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
              Cancel
            </button>
            <LoadingButton
              type="submit"
              className="btn btn-primary"
              icon={FiSave}
              loading={createMutation.isLoading || updateMutation.isLoading}
              loadingLabel={editingRow ? 'Updating…' : 'Creating…'}
            >
              {editingRow ? 'Update' : 'Create'}
            </LoadingButton>
          </div>
        </form>
      </Modal>
    </section>
  );
}
